<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import UIKit, { type ITableColumn, type ITableSortSettings, type TTableRow } from '@pocketprep/ui-kit'
import BrandColors from '@pocketprep/ui-kit/pocketprep-export.module.scss'
import type { Study, Teach } from '@pocketprep/types'
import { formatDate, getAssignmentStatus, getHoursMinutesSeconds, monthNames, stripHtmlTags } from '@/utils'
import { useRoute, useRouter } from 'vue-router'
import { assignmentModule } from '@/store/assignment/module'
import { examModule } from '@/store/exam/module'
import { instructorModule } from '@/store/instructor/module'
import { organizationModule } from '@/store/organization/module'
import { studentModule } from '@/store/student/module'
import { licenseModule } from '@/store/license/module'
import { statsModule } from '@/store/stats/module'
import { toastModule } from '@/store/toast/module'
import AssignmentFooter from '@/components/Assignments/AssignmentFooter.vue'
import AssignmentHead from '@/components/Assignments/AssignmentHead.vue'
import AssignmentSidebar from '@/components/Assignments/AssignmentSidebar.vue'
import ExamActivitySidePanel from '@/components/SidePanels/ExamActivity.vue'
import DeleteAssignmentModal from '@/components/Assignments/DeleteAssignmentModal.vue'
import QuestionView from '@/components/Questions/QuestionView.vue'
import type { TQuestion, TQuestions } from '@/store/types'
import AssignmentDates from '@/components/Assignments/AssignmentDates.vue'
import LegacyBanner from '@/components/Assignments/LegacyBanner.vue'
import { analyticsModule } from '@/store/analytics/module'
import { bundleModule } from '@/store/bundle/module'
import type { TFilter } from '@/components/Filters/FilterDropdown.vue'

const {
    Button: PocketButton,
    Table,
    TablePagination,
    Tag,
    OverflowTooltip,
    Icon,
    Errors,
} = UIKit

interface IMappedStudent extends TTableRow {
    studentId: string
    userId?: string
    studentNameOrEmail: string
    isCompleted: boolean
    timeSpent?: number
    dateCompleted?: string
    score?: number
    status: 'active' | 'pending' | 'archived'
    styles: { [key: string]: string }
    id: string
}

export type TSubjects = {
    [key: string]: {
        name: string
        count: number
        specialCount: number
    }
}

const router = useRouter()
const route = useRoute()

const isLoading = ref(false)
const showDeleteModal = ref(false)
const brandColors = BrandColors
const sortSettings = ref<ITableSortSettings>({
    column: {
        name: 'Student',
        propName: 'studentNameOrEmail',
    },
    direction: 1,
})
const onSubmitErrors = ref<string[]>([])
const openModalIds = ref<string[]>([])
const perPage = 10
const currentPage = ref(0)
const selectedStudentUserId = ref<{ studentId: string; studentUserId: string } | null>(null)
const isShowingExamActivitySidePanel = ref(false)
const rowHoverId = ref<string | number | null>(null)
const showQuestionReview = ref(false)
const questionFilterType = ref('answered')
const filterSubjects = ref<string[]>([])
const checkboxSubjects = ref<string[]>([])
const assignmentColumns: ITableColumn[] = [
    {
        name: 'Student',
        propName: 'studentNameOrEmail',
        styles: {
            width: '260px',
        },
    }, {
        name: 'Completed',
        propName: 'isCompleted',
        styles: {
            width: '100px',
        },
    }, {
        name: 'Date Completed',
        propName: 'dateCompleted',
        styles: {
            width: '108px',
        },
    }, {
        name: 'Time Spent',
        propName: 'timeSpent',
        styles: {
            width: '100px',
        },
    }, {
        name: 'Score',
        propName: 'score',
        styles: {
            width: '52px',
        },
    },
]

const assignments = computed(() => {
    return assignmentModule.getters.getCurrentOrgAssignments()
})

const assignment = computed(() => {
    const assignmentId = route.params.assignmentId
    const storedAssignment = typeof assignmentId === 'string' ? assignments.value[assignmentId] : undefined

    return storedAssignment
})

const currentOrg = computed(() => {
    return organizationModule.getters.getCurrentOrganization()
})

const currentOrgInstructor = computed(() => {
    return instructorModule.getters.getCurrentOrgInstructor()
})

const currentOrgStudents = computed(() => {
    return studentModule.getters.getCurrentOrgStudents()
})

const currentOrgStudentUsers = computed(() => {
    return studentModule.getters.getCurrentOrgStudentUsers()
})

const studentLicenseLib = computed(() => {
    return licenseModule.getters.getStudentLicenseLib()
})

const orgStudentsByUserIdLib = computed(() => {
    return Object.values(currentOrgStudents.value)
        .reduce<{ [userId: string]: Teach.Class.OrgStudentJSON }>((acc, student) => {
            if (!student.user || student.isArchived) {
                return acc
            }
            acc[student.user.objectId] = student

            return acc
        }, {})
})

const allExamMetadata = computed(() => {
    return examModule.getters.getExamMetadata()
})

const studentStats = computed(() => {
    return statsModule.getters.getStudentStats()
})

const currentExamMetadata = computed(() => {
    const assignmentMetadataId = assignment.value?.examMetadata.objectId
    if (!assignmentMetadataId) {
        return null
    }
    return allExamMetadata.value[assignmentMetadataId]
})

const isLegacyAssignment = computed(() => {
    return (!assignment.value?.availableDate && !assignment.value?.dueDate && assignment.value?.status !== 'draft')
})

const assignmentStatus = computed(() => {
    const availableDate = assignment.value?.availableDate?.iso
    const dueDate = assignment.value?.dueDate?.iso
    return isLegacyAssignment.value
        ? 'available'
        : assignment.value?.status === 'draft'
            ? 'draft'
            : getAssignmentStatus({ availableDate, dueDate })
})

const assignmentStats = computed(() => {
    return Object.entries(assignment.value?.attempts || {}).reduce((statsAcc, [ userId, attempt ]) => {
        const orgStudent = orgStudentsByUserIdLib.value[userId]
        const firstAttempt = attempt[0]
        if (!orgStudent || orgStudent.isArchived || !orgStudent.user) {
            return statsAcc
        }

        statsAcc.studentCount++
        statsAcc.incorrectCount += (firstAttempt?.incorrectCount || 0)
        statsAcc.correctCount += (firstAttempt?.correctCount || 0)
        statsAcc.timeSpent += firstAttempt?.durationSeconds || 0
        return statsAcc
    }, { correctCount: 0, incorrectCount: 0, studentCount: 0, timeSpent: 0 })
})

const assignmentStudentCompletedStats = computed(() => {
    if (!assignmentStats.value) {
        return null
    }
    return {
        studentsCompletedCount: assignmentStats.value.studentCount,
        studentsNotCompletedCount: mappedAssignmentStudents.value.length - assignmentStats.value.studentCount,
    }
})

const averageTimeSpent = computed(() => {
    const totalTimeSpent = assignmentStats.value.timeSpent
    const studentsTookAssignmentCount = assignmentStats.value.studentCount
    if (totalTimeSpent === 0 || !studentsTookAssignmentCount) {
        return 0
    } else {
        return totalTimeSpent / studentsTookAssignmentCount
    }
})

const overallScorePercentAsNumber = computed(() => {
    const correctCount = assignmentStats.value.correctCount
    const incorrectCount = assignmentStats.value.incorrectCount
    const total = correctCount + incorrectCount

    return total === 0 ? 0 : Math.round((correctCount / total) * 100)
})

const mappedAssignmentStudents = computed(() => {
    if (!assignment.value?.assignedOrgStudents?.length) {
        return []
    }
    return assignment.value.assignedOrgStudents.reduce((acc, os) => {
        const orgStudent = currentOrgStudents.value[os.objectId]
        if (!orgStudent) {
            return acc
        }
        const orgStudentUser = orgStudent.user && currentOrgStudentUsers.value[orgStudent.user.objectId]
        const studentId = orgStudent.objectId
        const userId = orgStudentUser?.objectId
        const studentExamLicense = studentLicenseLib.value[orgStudent.objectId]?.find(license =>
            license.examGuid === currentExamMetadata.value?.examGuid)
        const studentNameOrEmail = [ orgStudent.displayFirstName, orgStudent.displayLastName ].join(' ').trim()
            || [ orgStudentUser?.firstName, orgStudentUser?.lastName ].join(' ').trim()
            || studentExamLicense?.userEmail || ''
        const studentAssignmentAttempt = orgStudentUser
            && assignment.value?.attempts[orgStudentUser.objectId]
        const firstAttempt = studentAssignmentAttempt?.[0]
        const isCompleted = firstAttempt ? true : false
        const timeSpent = firstAttempt?.durationSeconds || 0
        const dateCompleted = firstAttempt?.attemptedAt
        const studentCorrectAnsweredCount = firstAttempt?.correctCount || 0
        const studentIncorrectAnsweredCount = firstAttempt?.incorrectCount || 0
        const studentTotalQuestionsAttemptedCount = studentCorrectAnsweredCount + studentIncorrectAnsweredCount
        const scorePercentAsNumber = isCompleted && studentTotalQuestionsAttemptedCount
            ? Math.round((studentCorrectAnsweredCount / studentTotalQuestionsAttemptedCount) * 100)
            : undefined
        const score = scorePercentAsNumber
        const isArchived = studentExamLicense?.status === 'archived'
        const status = isArchived
            ? 'archived'
            : !orgStudentUser ? 'pending' : 'active'
        const styles: { [key: string]: string } = { cursor: 'default' }
        if (isCompleted && (rowHoverId.value === studentId || studentId === route.query.studentId)) {
            styles.backgroundColor = brandColors.grayBackground as string
            styles.cursor = 'pointer'
        }

        acc.push({
            studentId,
            userId,
            studentNameOrEmail,
            isCompleted,
            timeSpent,
            dateCompleted,
            score,
            status,
            styles,
            id: studentId,
        })
        return acc
    }, [] as IMappedStudent[]).sort((a, b) => {
        if (sortSettings.value.column && a && b) {
            if (sortSettings.value.column.name === 'Student') {
                return sortSettings.value.direction * (a.studentNameOrEmail)
                    .localeCompare(b.studentNameOrEmail)
            }
            if (sortSettings.value.column.name === 'Completed') {
                return sortSettings.value.direction * (Number(a.isCompleted) - Number(b.isCompleted))
            }
            if (sortSettings.value.column.name === 'Date Completed') {
                if (!a.dateCompleted) {
                    return sortSettings.value.direction * 1
                }
                if (!b.dateCompleted) {
                    return sortSettings.value.direction * -1
                }
                const dateA = new Date(a.dateCompleted)
                const dateB = new Date(b.dateCompleted)
                return sortSettings.value.direction * (dateA.getTime()) - (dateB.getTime())
            }
            if (sortSettings.value.column.name === 'Time Spent') {
                return sortSettings.value.direction * ((a.timeSpent || 0) - (b.timeSpent || 0))
            }
            if (sortSettings.value.column.name === 'Score') {
                return sortSettings.value.direction * ((a.score || 0) - (b.score || 0))
            }
        }

        return -1
    })
})

const pagedAssignmentStudents = computed(() => {
    return mappedAssignmentStudents.value.slice(currentPage.value * perPage, (currentPage.value * perPage) + perPage)
})

const selectedStudentName = computed(() => {
    const studentUserId = selectedStudentUserId.value?.studentUserId
    const selectedStudentUser = studentUserId && currentOrgStudentUsers.value[studentUserId]

    return {
        firstName: selectedStudentUser ? selectedStudentUser.firstName : '',
        lastName: selectedStudentUser ? selectedStudentUser.lastName : '',
    }
})

const selectedStudentMappedAssignment = computed(() => {
    const studentId = selectedStudentUserId.value?.studentId
    const assignmentId = assignment.value?.objectId
    const studentUserId = selectedStudentUserId.value?.studentUserId
    if (!studentId || !assignmentId || !studentUserId) {
        return null
    }
    const assignmentQuiz = assignment.value.attempts[studentUserId]?.length === 1
    // if there is more than one attempt we don't want to fetch Quiz record
    // because it is most likely not going to the record for first attempt
        ? studentStats.value[studentId]?.allActivity
            .find(a => a.mode === 11 && a.assignment && a.assignment.objectId === assignmentId)
        : null

    // if no Quiz record, use high level information from assignment attemps
    if (!assignmentQuiz) {
        const firstAssignmentAttempt = assignment.value.attempts[studentUserId]?.[0]
        if (!firstAssignmentAttempt) {
            return null
        } else {
            const date = new Date(firstAssignmentAttempt.attemptedAt)
            const dateMonth = monthNames[date.getMonth()]?.substring(0, 3) || ''
            const dateDay = date.getDate() || ''
            const dateYear = date.getFullYear() || ''
            const totalQuestionsCount = firstAssignmentAttempt.correctCount + firstAssignmentAttempt.incorrectCount
            const score = (firstAssignmentAttempt.correctCount / totalQuestionsCount) * 100
            return {
                name: 'Assignment',
                date: `${dateMonth} ${dateDay}`,
                year: `${dateYear}`,
                score: Math.round(score),
                scoreRange: getScoreRange(score),
                durationSeconds: firstAssignmentAttempt.durationSeconds || 0,
                studentId,
                assignmentName: assignment.value.name,
                answeredCount: totalQuestionsCount,
                correctCount: firstAssignmentAttempt.correctCount,
                incorrectCount: firstAssignmentAttempt.incorrectCount,
            }
        }
    }
    const date = new Date(assignmentQuiz.date)
    const answers = assignmentQuiz.answers
    const dateMonth = monthNames[date.getMonth()]?.substring(0, 3) || ''
    const dateDay = date.getDate() || ''
    const dateYear = date.getFullYear() || ''

    return {
        name: 'Assignment',
        date: `${dateMonth} ${dateDay}`,
        year: `${dateYear}`,
        score: assignmentQuiz.score,
        scoreRange: getScoreRange(assignmentQuiz.score),
        answers,
        durationSeconds: assignmentQuiz.duration || 0,
        quizId: assignmentQuiz.quizId,
        studentId,
        assignmentName: assignment.value.name,
    }
})

const currentExamQuestionsBySerial = computed(() => {
    return examModule.getters.getCurrentExamQuestionsBySerial()
})

const activityQuestions = computed(() => {
    const activityAnswers = selectedStudentMappedAssignment.value?.answers

    const questionsForActivity: TQuestion[] = []

    if (activityAnswers) {
        activityAnswers.forEach(quizAnswer => {
            const question = currentExamQuestionsBySerial.value[quizAnswer.questionSerial]
            if (question) {
                questionsForActivity.push({
                    question,
                    answer: quizAnswer,
                })
            }
        })
    }
    return questionsForActivity
})

const questions = computed(() => {
    const assignmentQuestions = activityQuestions.value
    return assignmentQuestions.reduce<TQuestions>((acc, question) => {
        const isAnswered = question.answer
        const isFlagged = isQuestionFlagged(question.question.serial)
        const isCorrect = question.answer?.isCorrect
        const subject = question.question.subject as Study.Class.SubjectJSON
        question.question.prompt = stripHtmlTags(question.question.prompt)
        const scenarioLabel = questionScenarioPartLabel(question.question)
        if (scenarioLabel) {
            question.scenarioPartLabel = scenarioLabel
        }

        // Are filters enabled? If so.. is the question's subject in the filter?
        const checkFilterSubjects = filterSubjects.value.length === 0
            || filterSubjects.value.length > 0 && filterSubjects.value.includes(subject.name)

        // Is the question answered?
        if (isAnswered && checkFilterSubjects) {
            acc.answered?.push(question)
        }

        // Is the question flagged?
        if (isFlagged && checkFilterSubjects) {
            acc.flagged?.push(question)
        }

        // Is the question answered + incorrect?
        if (!isCorrect && isAnswered && checkFilterSubjects) {
            acc.incorrect?.push(question)
        }

        // Is the question correct?
        if (isCorrect && checkFilterSubjects) {
            acc.correct?.push(question)
        }
        return acc
    }, { answered: [], flagged: [], incorrect: [], correct: [] })
})

const currentQuestion = computed(() => {
    const currentFilterQuestions = questions.value[questionFilterType.value]
    const currentQuestionIndex = questionIndex.value
    if (currentFilterQuestions && questionIndex.value !== undefined && questionIndex.value > -1) {
        if (currentQuestionIndex !== undefined && currentFilterQuestions.length > 0
            && currentFilterQuestions[currentQuestionIndex]) {
            return currentFilterQuestions[currentQuestionIndex].question
        }
    }
    return undefined
})

const currentSerial = computed(() => {
    return route.query.questionSerial || questions.value[questionFilterType.value]?.[0]?.question.serial || ''
})

const currentSubject = computed(() => {
    const subject = currentQuestion.value?.subject as Study.Class.SubjectJSON | undefined
    return subject?.name
})

const questionIndex = computed(() => {
    return questions.value[questionFilterType.value]?.length
        ? questions.value[questionFilterType.value]?.findIndex(q => q.question.serial === currentSerial.value)
        : -1
})

const assignmentSubjectIds = computed(() => {
    return assignment.value?.config.subjectIds
})

const currentExamMetadataKnowledgeAreas = computed(() => {
    return currentExamMetadata.value?.knowledgeAreas
})

const currentExamSubjects = computed(() => {
    return examModule.getters.getCurrentExamSubjects()
})

const assignmentSubjects = computed(() => {
    const subjectNames = assignmentSubjectIds.value?.map(subjectId =>
        currentExamSubjects.value[subjectId]?.name.replace(/\./g, '_'))
    const knowledgeAreas = currentExamMetadataKnowledgeAreas.value
    if (!subjectNames || !knowledgeAreas) {
        return undefined
    }

    const assignmentKnowledgeAreas: Study.Class.ExamMetadataJSON['knowledgeAreas'] = {}

    for (const key in knowledgeAreas) {
        if (subjectNames.includes(key) && knowledgeAreas[key]) {
            assignmentKnowledgeAreas[key] = knowledgeAreas[key]
        }
    }
    return assignmentKnowledgeAreas
})

const currentStudentStats = computed(() => {
    const studentId = selectedStudentUserId.value?.studentId
    if (!studentId) {
        return null
    }
    return studentStats.value[studentId]
})

const studentAnsweredQuestions = computed(() => {
    if (!currentStudentStats.value) {
        return null
    }
    if (filterSubjects.value.length && currentStudentStats.value.answeredQuestions.length) {
        return currentStudentStats.value.answeredQuestions.filter(q => {
            const s = q.question && q.question.subject as Study.Class.SubjectJSON
            return filterSubjects.value.includes(q.question && s.name)
        })
    }
    return currentStudentStats.value.answeredQuestions
})

const studentFlaggedQuestions = computed(() => {
    if (!currentStudentStats.value) {
        return null
    }
    const flaggedSerials = currentStudentStats.value.flaggedQuestions.map(q => q.serial)

    const flaggedQuestions: TQuestion[] = []

    flaggedSerials.forEach(serial => {
        if (!studentAnsweredQuestions.value) {
            return null
        }
        const flaggedQuestion = studentAnsweredQuestions.value.find(q => q.question.serial === serial)

        if (flaggedQuestion) {
            flaggedQuestions.push(flaggedQuestion)
        } else if (currentExamQuestionsBySerial.value[serial]) {
            flaggedQuestions.push({
                question: currentExamQuestionsBySerial.value[serial],
            })
        }
    })
    if (flaggedQuestions && filterSubjects.value.length) {
        return flaggedQuestions.filter(q => {
            const s = q.question && q.question.subject as Study.Class.SubjectJSON
            return filterSubjects.value.includes(q.question && s.name)
        })
    }
    return flaggedQuestions
})

const isCurrentQuestionFlagged = computed(() => {
    return studentFlaggedQuestions.value?.find(q =>
        q.question && q.question.serial === currentSerial.value)
        ? true
        : false
})

const currentQuizTab = computed(() => {
    const questionType = route.query.type as string | undefined
    const capitalizedType = questionType?.[0] ? questionType[0].toLocaleUpperCase() + questionType.slice(1) : 'Answered'
    return capitalizedType as 'Answered' | 'Flagged' | 'Incorrect' | 'Correct'
})

const questionScenarioPartLabel = (question: Study.Class.QuestionJSON) => {
    return examModule.getters.getScenarioPartLabel(question)
}

const isQuestionFlagged = (serial: string) => {
    return !!studentFlaggedQuestions.value?.find(question => question.question.serial === serial)
}

const rowEnter = (row: TTableRow) => {
    rowHoverId.value = row.id
}

const rowLeave = (row: TTableRow) => {
    if (row.id === rowHoverId.value) {
        rowHoverId.value = null
    }
}

const formattedTimeStudied = (timeSpentInSeconds: number) => {
    const { hours, minutes, seconds } = getHoursMinutesSeconds(timeSpentInSeconds)

    return hours
        ? `${hours}h ${minutes}m`
        : minutes
            ? `${minutes}m ${seconds}s`
            : `${seconds}s`
}

const updateSortSettings = (newSortSettings: ITableSortSettings) => {
    sortSettings.value = newSortSettings
}

const changePage = (newPage: number) => {
    currentPage.value = newPage
}

const getScoreColor = (score: number) => {
    if (score <= 50) {
        return brandColors.pepper
    } else if (score < 75) {
        return brandColors.bananaBread
    } else {
        return brandColors.cadaverous
    }
}

const getScoreRange = (score: number) => {
    if (score <= 50) {
        return 'low'
    } else if (score < 75) {
        return 'medium'
    } else {
        return 'high'
    }
}

const deleteAssignment = async () => {
    isLoading.value = true
    const assignmentId = assignment.value?.objectId
    if (!assignmentId) {
        return
    }
    try {
        await assignmentModule.actions.deleteAssignment({ assignmentId })
        await router.push({ name: 'assignments' })
        toastModule.actions.displayToast({
            title: 'Assignment deleted.',
        })

        if (currentOrg.value) {
            analyticsModule.actions.amplitudeTrack('Assignment Deleted', {
                organizationId: currentOrg.value.objectId,
                hasOrgPurchasedSeats: licenseModule.getters.getCurrentOrgLicenses().length ? true : false,
                examGuid: allExamMetadata.value[assignment.value.examMetadata.objectId]?.examGuid,
                bundleId: bundleModule.getters.getExamBundle(assignment.value.examMetadata.objectId)?.objectId || '',
                userIsOwnerOfAssignment: assignments.value[assignmentId]?.creator
                    .objectId === currentOrgInstructor.value?.user.objectId,
                assignmentStatus: assignment.value.status === 'draft'
                    ? 'draft'
                    : getAssignmentStatus({
                        availableDate: assignment.value.availableDate?.iso,
                        dueDate: assignment.value.dueDate?.iso,
                    }),
            })
        }
    } catch (err) {
        onSubmitErrors.value = []
        onSubmitErrors.value.push('There was a problem deleting this assignment. Please try again.')

        showDeleteModal.value = false
    }

    isLoading.value = false
}

const editAssignment = async () => {
    const assignmentId = assignment.value?.objectId
    if (!assignmentId) {
        return
    }
    await router.push({ name: 'edit-assignment',
        params: { assignmentId } })
}

const closeQuestionReview = () => {
    showQuestionReview.value = false
}

const closeQuizReview = async () => {
    await router.replace({ query: { studentId: undefined } })
    isShowingExamActivitySidePanel.value = false
    selectedStudentUserId.value = null
}

const studentClicked = async (student: { studentId: string; isCompleted?: boolean }) => {
    if (student.isCompleted === false) {
        return
    }
    const studentId = student.studentId
    const studentUserId = currentOrgStudents.value[studentId]?.user?.objectId
    if (!currentExamMetadata.value || !studentUserId) {
        return
    }
    const studentUserIdPaylod = { studentId, studentUserId }
    selectedStudentUserId.value = studentUserIdPaylod

    await statsModule.actions.fetchBatchStudentStats({
        students: [ studentUserIdPaylod ],
        compositeKey: currentExamMetadata.value?.compositeKey,
        examMetadataId: currentExamMetadata.value.objectId,
    })

    await router.push({ query: { ...route.query, studentId: studentId } })

    if (currentOrg.value) {
        analyticsModule.actions.amplitudeTrack('Assignment Student Clicked', {
            organizationId: currentOrg.value.objectId,
            hasOrgPurchasedSeats: licenseModule.getters.getCurrentOrgLicenses().length ? true : false,
            examGuid: currentExamMetadata.value.examGuid,
            bundleId: bundleModule.getters.getExamBundle(currentExamMetadata.value.objectId)?.objectId || '',
            studentId: selectedStudentUserId.value.studentId,
            studentUserId: selectedStudentUserId.value.studentUserId,
        })
    }

    isShowingExamActivitySidePanel.value = true
}

const questionClicked = async (params: { questionSerial: string; type?: string }) => {
    await router.push({
        query: {
            ...route.query,
            questionSerial: params.questionSerial,
            type: params.type,
        },
    })
    questionFilterType.value = params.type || 'answered'
    showQuestionReview.value = true
}

const quizTabClicked = async (questionType: 'Answered' | 'Flagged' | 'Incorrect' | 'Correct') => {
    await router.push({ query: {
        ...route.query,
        type: questionType.toLocaleLowerCase(),
    } })
}

const updateFilter = (filterOptions: TFilter) => {
    questionFilterType.value = filterOptions.questionType
    filterSubjects.value = filterOptions.trimmedSubjects
    checkboxSubjects.value = filterOptions.subjects
    const query: { [key: string]: string | string[] } = {
        ...route.query,
        type: filterOptions.questionType,
        subject: filterOptions.trimmedSubjects,
    }
    const isQuestionInFilter = questions.value[filterOptions.questionType]?.find(q =>
        q.question.serial === currentSerial.value)
    if (!isQuestionInFilter) {
        const firstQuestionInNewFilter = questions.value[filterOptions.questionType]?.[0]
        query.questionSerial = firstQuestionInNewFilter?.question.serial
            ? firstQuestionInNewFilter?.question.serial
            : ''
    }
    router.replace({ query })
}

const updateSerial = () => {
    router.replace({
        query: {
            ...route.query,
            questionSerial: currentSerial.value,
        },
    })
}

const saveOpenModal = (modalId: string) => {
    openModalIds.value.push(modalId)
}

const removeOpenModal = (modalId: string) => {
    openModalIds.value = openModalIds.value.filter(id => id !== modalId)
}

watch(currentSerial, updateSerial)

onMounted(async () => {
    isLoading.value = true
    await Promise.all([
        assignmentModule.actions.fetchAssignments(),
        instructorModule.actions.fetchCurrentOrgInstructor(),
        instructorModule.actions.fetchCurrentOrgInstructorUsers(),
        organizationModule.actions.fetchCurrentOrganization(),
        studentModule.actions.fetchCurrentOrgStudentUsers(),
    ])
    if (currentExamMetadata.value && currentOrg.value) {
        await examModule.actions.fetchQuestionsByExam({
            compositeKey: currentExamMetadata.value.compositeKey,
            organizationId: currentOrg.value.objectId,
            examMetadataId: currentExamMetadata.value.objectId,
        }, true)
    }

    if (route.query.studentId) {
        const studentId = route.query.studentId as string
        await studentClicked({ studentId })
    }
    if (route.query.questionSerial) {
        const questionSerial = route.query.questionSerial as string
        const type = route.query.type as string | undefined
        if (type) {
            questionFilterType.value = type
        }
        await questionClicked({ questionSerial, type })
    }

    if (currentOrg.value && currentExamMetadata.value) {
        analyticsModule.actions.amplitudeTrack('Assignment Viewed', {
            organizationId: currentOrg.value.objectId,
            hasOrgPurchasedSeats: licenseModule.getters.getCurrentOrgLicenses().length ? true : false,
            examGuid: currentExamMetadata.value.examGuid || '',
            bundleId: bundleModule.getters.getExamBundle(currentExamMetadata.value.objectId)?.objectId || '',
            assignmentStatus: getAssignmentStatus({
                availableDate: assignment.value?.availableDate?.iso,
                dueDate: assignment.value?.dueDate?.iso,
            }),
        })
    }

    isLoading.value = false
})
</script>

<template>
    <DeleteAssignmentModal
        v-if="showDeleteModal && assignmentStudentCompletedStats"
        :assignmentStudentCompletedStats="assignmentStudentCompletedStats"
        @close="showDeleteModal = false"
        @delete="deleteAssignment"
    />
    <div class="view-assignment" v-if="assignment && !isLoading">
        <QuestionView
            v-if="showQuestionReview"
            :questions="questions"
            :current-question="currentQuestion"
            :is-current-question-flagged="isCurrentQuestionFlagged"
            :current-subject="currentSubject"
            :quiz-subjects="assignmentSubjects"
            :quiz-length="questions[questionFilterType]?.length"
            :question-index="questionIndex || 0"
            :previous-page="'Assignment'"
            :filter-subjects="filterSubjects"
            :type="questionFilterType"
            :student-name="selectedStudentName"
            :activity-name="'Assignment'"
            :activity-date="assignment.availableDate?.iso"
            :trapFocus="openModalIds[openModalIds.length - 1] === 'questionView'"
            @mounted="saveOpenModal('questionView')"
            @filterUpdate="updateFilter"
            @close="removeOpenModal('questionView'); closeQuestionReview()"
        />
        <ExamActivitySidePanel
            v-if="isShowingExamActivitySidePanel && selectedStudentMappedAssignment"
            :student="`${selectedStudentName?.firstName} ${selectedStudentName?.lastName}`"
            :exam-activity="selectedStudentMappedAssignment"
            :default-active-tab="currentQuizTab"
            :trapFocus="openModalIds[openModalIds.length - 1] === 'activitySidepanel'"
            @mounted="saveOpenModal('activitySidepanel')"
            @close="removeOpenModal('activitySidepanel'); closeQuizReview()"
            @questionClicked="questionClicked"
            @tabClicked="quizTabClicked"
        />
        <AssignmentHead>
            <template #right>
                <AssignmentDates
                    v-if="assignment && assignmentStatus"
                    :isDark="true"
                    :status="assignmentStatus"
                    :availableDate="assignment.availableDate?.iso"
                    :dueDate="assignment.dueDate?.iso"
                    :isLegacy="isLegacyAssignment"
                    class="view-assignment__date-status"
                />
            </template>
        </AssignmentHead>
        <AssignmentSidebar :assignmentId="assignment.objectId" />
        <div class="view-assignment__content-container">
            <div class="view-assignment__content">
                <Errors :errors="onSubmitErrors" v-if="onSubmitErrors.length" />
                <LegacyBanner v-if="isLegacyAssignment" />
                <div class="view-assignment__head">
                    <div class="view-assignment__name">
                        {{ assignment.name }}
                    </div>
                    <div class="view-assignment__stat">
                        <div class="view-assignment__stat-value">
                            {{ assignmentStats.studentCount || '--' }}
                            <span class="view-assignment__stat-divider">/</span>
                            {{ assignment.assignedOrgStudents?.length || 0 }}
                        </div>
                        <div class="view-assignment__stat-name">
                            Students Completed
                        </div>
                    </div>
                    <div class="view-assignment__stat">
                        <div class="view-assignment__stat-value">
                            {{ formattedTimeStudied(averageTimeSpent) }}
                        </div>
                        <div class="view-assignment__stat-name">
                            Average Time Spent
                        </div>
                    </div>
                    <div class="view-assignment__stat view-assignment__stat--score">
                        <div class="view-assignment__stat-value">
                            {{ `${overallScorePercentAsNumber || '--'}%` }}
                        </div>
                        <div class="view-assignment__stat-name">
                            Overall Score
                        </div>
                    </div>
                </div>
                <Table
                    :columns="assignmentColumns"
                    :rows="pagedAssignmentStudents"
                    :sort-settings="sortSettings"
                    :sort-toggle-order="[ 1, -1 ]"
                    :show-header="false"
                    @sort="updateSortSettings"
                    :table-column-labels-styles="{
                        backgroundColor: brandColors.grayBackground || '',
                        fontSize: '12px',
                        color: brandColors.brandBlack || '',
                    }"
                    :table-cell-styles="{
                        height: '20px',
                    }"
                    @rowClicked="studentClicked({
                        studentId: $event.studentId as string,
                        isCompleted: $event.isCompleted as boolean})"
                    @rowEnter="rowEnter"
                    @rowLeave="rowLeave"
                >
                    <template #tableCellValue="{ row, column }">
                        <template v-if="column.propName === 'studentNameOrEmail'">
                            <div class="view-assignment__student-name-row">
                                <OverflowTooltip
                                    class="view-assignment__student-name"
                                    :class="{'view-assignment__student-name--pending-or-archived':
                                        row.status === 'pending' || row.status === 'archived' }"
                                >
                                    {{ row.studentNameOrEmail }}
                                </OverflowTooltip>
                                <Tag
                                    v-if="row.status === 'pending'"
                                    class="view-assignment__pending-status-tag"
                                    size="small"
                                    :is-pending-tag="true"
                                    :tag-border="`1px solid ${brandColors.bananaBread}`"
                                >
                                    <template #tagMessage>
                                        Pending
                                    </template>
                                </Tag>
                                <Tag
                                    v-else-if="row.status === 'archived'"
                                    class="view-assignment__archived-status-tag"
                                    size="small"
                                    :is-archived-tag="true"
                                >
                                    <template #tagMessage>
                                        Archived
                                    </template>
                                </Tag>
                            </div>
                        </template>
                        <template v-if="column.propName === 'isCompleted'">
                            <div>
                                <Icon
                                    v-if="row.isCompleted"
                                    class="view-assignment__completed-icon"
                                    type="correct"
                                />
                                <template v-else>
                                    --
                                </template>
                            </div>
                        </template>
                        <template v-if="column.propName === 'timeSpent'">
                            {{ formattedTimeStudied(row.timeSpent || 0) }}
                        </template>
                        <template v-if="column.propName === 'dateCompleted'">
                            {{ row.dateCompleted ? formatDate(row.dateCompleted) : '--' }}
                        </template>
                        <template v-if="column.propName === 'score'">
                            <div v-if="row.score || row.score === 0" class="view-assignment__score-cell">
                                <span>{{ `${row.score}%` }}</span>
                                <div
                                    class="view-assignment__indicator-dot"
                                    :style="{ backgroundColor: `${getScoreColor(row.score)}` }"
                                />
                            </div>
                            <span v-else>--%</span>
                        </template>
                    </template>
                </Table>
                <TablePagination
                    v-if="pagedAssignmentStudents.length"
                    class="view-assignment__pagination"
                    :per-page="perPage"
                    :current-page="currentPage"
                    :total="mappedAssignmentStudents.length"
                    unit="Students"
                    @changePage="changePage"
                />
            </div>
        </div>
        <AssignmentFooter>
            <template #left>
                <PocketButton
                    type="secondary"
                    class="view-assignment__delete-btn"
                    @click="showDeleteModal = true"
                >
                    Delete Assignment
                </PocketButton>
            </template>
            <template #right>
                <RouterLink
                    v-slot="{ navigate }"
                    custom
                    :to="{ name: 'assignments' }"
                >
                    <PocketButton
                        type="secondary"
                        @click="() => navigate"
                    >
                        Back
                    </PocketButton>
                </RouterLink>
                <PocketButton
                    v-if="assignment"
                    class="view-assignment__edit-btn"
                    @click="editAssignment"
                >
                    Edit Assignment
                </PocketButton>
            </template>
        </AssignmentFooter>
    </div>
</template>

<style lang="scss" scoped>
@use 'sass:color';

.view-assignment {
    height: 100vh;
    overflow: scroll;

    &__content-container {
        margin: 54px 0 80px 320px;
        padding-top: 32px;
    }

    &__content {
        width: 792px;
        margin: auto;
    }

    &__head {
        width: 747px;
        margin: 0 auto 60px;
        display: flex;
    }

    &__name {
        min-height: 68px;
        width: 238px;
        font-size: 26px;
        line-height: 34px;
        font-weight: 600;
        display: flex;
        flex-direction: column;
        justify-content: flex-end;
    }

    &__stat {
        display: flex;
        flex-direction: column;
        align-items: center;
        margin: 0 20px;
        width: 120px;
        justify-content: flex-end;

        &--score {
            width: 100px;
        }
    }

    &__stat-value {
        font-size: 24px;
        line-height: 33px;
        font-weight: 600;
        letter-spacing: 0.24px;
    }

    &__stat-name {
        color: $ash;
        font-size: 12px;
        line-height: 16px;
    }

    &__stat-divider {
        color: $pewter;
        margin: 0 10px;
    }

    &__delete-btn {
        margin-left: 30px;

        :deep(.uikit-btn__content) {
            font-weight: 600;
            color: $pepper;
        }
    }

    &__edit-btn {
        margin-right: 30px;
        margin-left: 8px;
    }

    &__pagination {
        justify-content: flex-end;
        margin-top: 17px;
    }

    &__student-name-row {
        display: flex;
    }

    &__student-name {
        width: 204px;
        margin-right: 4px;

        &--pending-or-archived {
            color: $ash;
        }
    }

    &__completed-icon {
        color: $steel;
    }

    &__score-cell {
        display: flex;
        align-items: center;
    }

    &__indicator-dot {
        margin-left: 6px;
        height: 6px;
        width: 6px;
        border-radius: 100%;
        position: relative;
        bottom: 1px;
    }

    &__date-status {
        margin-right: 44px;

        &--light {
            margin-right: 0;
            color: $ash;
        }
    }

    :deep(table.uikit-table__content) {
        border: 1px solid rgba($pewter, 0.3);
        border-radius: 4px;
    }

    :deep(.uikit-table__row:last-child) {
        border-bottom: none;
    }
}
</style>
